import clsx from 'clsx';
import { Button, IconButton, Theme, Tooltip } from '@mui/material';
import { Feature } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { colorPalette } from '@qualifio/ui-commons';
import { faBracketsCurly } from '@fortawesome/pro-solid-svg-icons/faBracketsCurly';
import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck';
import { faCode } from '@fortawesome/pro-solid-svg-icons/faCode';
import { faRedo } from '@fortawesome/pro-solid-svg-icons/faRedo';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faUndo } from '@fortawesome/pro-solid-svg-icons/faUndo';
import { makeStyles } from '@mui/styles';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ACTION_PANEL_ID } from '../../config';
import { CustomCodeModal } from '../custom-code/CustomCodeModal';
import { JsonEditorModal } from '../json-editor/JsonEditorModal';
import { PublishButton } from '../../../shared/publish-button';
import { useCurrentTemplate, usePublishTemplate, useSaveTemplate } from '../../hooks';
import { useEditorStore } from '../../store';
import { useFeatures } from '../../../features/FeaturesContext';
import { useProgram } from '../../../loyalty/ProgramContext';

const useStyles = makeStyles<Theme>(({ spacing, palette }) => ({
  root: {
    backgroundColor: colorPalette.marine.main,
    color: palette.secondary.contrastText,
    display: 'flex',
    justifyContent: 'space-between',
    padding: spacing(1),
  },
  storeButtons: {
    display: 'flex',
    '&> *': {
      marginLeft: spacing(2),
    },
  },
  button: {
    fontSize: 20,
    opacity: 0.4,
    color: 'white',
  },
  buttonActive: {
    opacity: 1,
  },
  disabledButton: {
    backgroundColor: `${colorPalette.green.main}` + ' !important',
  },
  customCodeButton: {
    backgroundColor: `${colorPalette.marine.lightest}` + ' !important',
    color: 'white !important',
    marginLeft: `${spacing(1)} !important`,
  },
}));

/**
 * EditorToolbar - Main toolbar with undo/redo, custom code, save and publish actions.
 */
export function EditorToolbar(): React.ReactElement {
  const { t } = useTranslation();
  const { features } = useFeatures();
  const { program } = useProgram();
  const { write: templateWrite } = features[Feature.TEMPLATE];
  const { enabled: templateJsonEnabled } = features[Feature.TEMPLATE_JSON];
  const classes = useStyles();

  // Zustand store
  const editorService = useEditorStore(state => state.editorService);
  const isDirty = useEditorStore(state => state.isDirty);
  const getTemplateJSON = useEditorStore(state => state.getTemplateJSON);

  // React Query
  const { data: template } = useCurrentTemplate();
  const { mutateAsync: saveTemplate, isPending: isSaving } = useSaveTemplate();
  const { mutateAsync: publishTemplate, isPending: isPublishing } = usePublishTemplate();

  // Derived state
  const isPublished = !!program.templateId && template?.id === program.templateId;
  const isPublishable = !!template?.id && !isDirty;
  const loading = isSaving || isPublishing;

  // Local state
  const [hasUndo, setHasUndo] = useState(false);
  const [hasRedo, setHasRedo] = useState(false);
  const [customCodeOpen, setCustomCodeOpen] = useState(false);
  const [jsonEditorOpen, setJsonEditorOpen] = useState(false);

  // Handlers
  const handleUndo = useCallback(() => {
    setHasUndo(!!editorService?.editor.UndoManager.hasUndo());
    setHasRedo(!!editorService?.editor.UndoManager.hasRedo());
  }, [editorService]);

  const handleSave = useCallback(async () => {
    const newTemplate = getTemplateJSON();
    await saveTemplate(newTemplate);
  }, [getTemplateJSON, saveTemplate]);

  const handlePublish = useCallback(async () => {
    if (!template?.id) {
      throw new Error('Cannot publish unsaved template');
    }
    await publishTemplate(template.id);
  }, [template?.id, publishTemplate]);

  // Effects
  useEffect(() => {
    editorService?.on('update', handleUndo);
    editorService?.on('undo', handleUndo);
    editorService?.on('redo', handleUndo);
    handleUndo();

    return () => {
      editorService?.off('update', handleUndo);
      editorService?.off('undo', handleUndo);
      editorService?.off('redo', handleUndo);
    };
  }, [editorService, handleUndo]);

  return (
    <div className={classes.root}>
      <div id={ACTION_PANEL_ID}>
        <IconButton
          className={clsx(classes.button, hasUndo && classes.buttonActive)}
          onClick={() => editorService?.editor.runCommand('core:undo')}
        >
          <FontAwesomeIcon icon={faUndo} style={{ color: 'white' }} />
        </IconButton>
        <IconButton
          className={clsx(classes.button, hasRedo && classes.buttonActive)}
          onClick={() => editorService?.editor.runCommand('core:redo')}
        >
          <FontAwesomeIcon icon={faRedo} style={{ color: 'white' }} />
        </IconButton>
        <CustomCodeModal isOpen={customCodeOpen} onClose={() => setCustomCodeOpen(false)} />
        <Button
          className={classes.customCodeButton}
          variant="rounded"
          startIcon={<FontAwesomeIcon icon={faCode} />}
          onClick={() => setCustomCodeOpen(true)}
        >
          {t('editor.actions.custom-code', 'Custom code')}
        </Button>
        {templateJsonEnabled && (
          <>
            <JsonEditorModal isOpen={jsonEditorOpen} onClose={() => setJsonEditorOpen(false)} />
            <Button
              className={classes.customCodeButton}
              variant="rounded"
              startIcon={<FontAwesomeIcon icon={faBracketsCurly} />}
              onClick={() => setJsonEditorOpen(true)}
            >
              {t('editor.actions.json-editor', 'JSON Editor')}
            </Button>
          </>
        )}
      </div>
      <div className={classes.storeButtons}>
        {isPublishable && !loading && (
          <PublishButton
            onClick={handlePublish}
            disabled={isPublished}
            className={classes.disabledButton}
          />
        )}
        {(!isPublishable || isDirty || loading) && (
          <Tooltip
            title={templateWrite ? '' : t('shared.action.permission.required')}
            placement="left"
          >
            <span>
              <Button
                startIcon={
                  <FontAwesomeIcon
                    fontSize="inherit"
                    icon={loading ? faSpinner : faCheck}
                    spin={loading}
                  />
                }
                className={classes.disabledButton}
                color="success"
                onClick={handleSave}
                disabled={loading || !templateWrite || !isDirty}
                id={'save-button-id'}
              >
                {t('editor.save.button')}
              </Button>
            </span>
          </Tooltip>
        )}
      </div>
    </div>
  );
}
